import React from "react";
import { Breadcrumbs, Typography, Link, Button, useTheme } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useLocation, useNavigate } from "react-router-dom";
import { Vaccines } from "@mui/icons-material";

const pathNameMap = {
  medical_history: "ประวัติการให้บริการ",
  pets: "สัตว์เลี้ยง",
  service: "การให้บริการ",
  vaccine: "ฉีดวัคซีน",
  dashboard: "แดชบอร์ด",
  users: "จัดการบุคลากร",
  register: "เพิ่มบุคลากร",
  microchip: "ฝังไมโครชิพ",
  parasite: "การป้องกันพยาธิ",
  create: "สร้างใหม่",
  history: "ประวัติการรักษา",
  list: "รายชื่อ",
  owner: "เจ้าของสัตว์เลี้ยง",
  connect: "เชื่อมต่อ",
  "connection-requests": "รายการขอเชื่อมต่อ",
  bow_medical_history: "ประวัติการรักษาส่วนกลาง",
  hospitals: "โรงพยาบาลสัตว์",
  profile: 'ข้อมูลผู้ใช้'
};

export default function AppBreadcrumbs() {
  const location = useLocation();
  const navigate = useNavigate();
  const pathnames = location.pathname.split("/").filter((x) => x);
  const theme = useTheme();

  // If on dashboard or root, we might not need breadcrumbs at all, 
  // but let's keep it for consistency.
  if (pathnames.length === 0 || (pathnames.length === 1 && pathnames[0] === "dashboard")) {
    return null;
  }

  const breadcrumbs = [];

  for (let i = 0; i < pathnames.length; i++) {
    const segment = pathnames[i];
    let to = `/${pathnames.slice(0, i + 1).join("/")}`;
    let label = pathNameMap[segment];

    // Redirect parent routes that lack an index page to their default list pages
    if (to === "/pets") to = "/pets/list";
    if (to === "/owner") to = "/owner/list";
    if (to === "/service") to = "/service/vaccine";

    // Handle redundant list/create segments if needed
    if (segment === "list" && pathnames[i - 1]) {
      // e.g. /pets/list -> keep as is or skip. Let's make it more descriptive
      label = `รายชื่อ${pathNameMap[pathnames[i - 1]] || ""}`;
    }

    if (segment === "create" && pathnames[i - 1]) {
      label = `เพิ่ม${pathNameMap[pathnames[i - 1]] || ""}ใหม่`;
    }

    // Handle IDs (non-mapped segments that are at the correct position)
    if (!label) {
      if (pathnames[i - 1] === "connection-requests") {
        label = "รายละเอียดคำขอ";
      } else if (pathnames[i - 1] === "pets") {
        label = "รายละเอียดสัตว์เลี้ยง";
      } else if (pathnames[i - 1] === "owner") {
        label = "แก้ไขข้อมูลเจ้าของ";
      } else if (pathnames[i - 1] === "medical_history" || pathnames[i - 1] === "users") {
        label = "รายละเอียด";
      } else if (pathnames[i - 2] === "bow_medical_history") {
        label = "รายละเอียด";
      } else {
        label = segment; // Fallback to raw segment
      }
    }

    // Override linking for bow_medical_history so it goes back to PetProfile
    if (segment === "bow_medical_history") {
      if (location.state?.pet?.id) {
        to = `/pets/${location.state.pet.id}`;
      } else {
        to = "/pets/list";
      }
    }

    breadcrumbs.push({
      label,
      to,
      isLast: i === pathnames.length - 1,
    });
  }

  return (
    <Breadcrumbs
      separator={<NavigateNextIcon fontSize="small" />}
      aria-label="breadcrumb"
      sx={{
        backgroundColor:
          theme.palette.mode === "dark"
            ? theme.palette.background.paper
            : "#eff1f3",
        p: "10px 16px",
        mb: 2,
        borderRadius: 2,
        "& .MuiBreadcrumbs-ol": {
          alignItems: "center",
        },
      }}
    >
      <Link
        underline="hover"
        color="inherit"
        onClick={() => navigate("/dashboard")}
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          fontSize: "0.9rem",
          fontWeight: 500,
        }}
      >
        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
        หน้าหลัก
      </Link>

      {breadcrumbs.map(({ label, to, isLast }) =>
        isLast ? (
          <Typography
            key={to}
            color="text.primary"
            sx={{ fontSize: "0.9rem", fontWeight: 600 }}
          >
            {label}
          </Typography>
        ) : (
          <Link
            key={to}
            underline="hover"
            color="primary"
            onClick={() => navigate(to)}
            sx={{
              cursor: "pointer",
              fontSize: "0.9rem",
              fontWeight: 500,
            }}
          >
            {label}
          </Link>
        )
      )}
    </Breadcrumbs>
  );
}
